import React, { useMemo } from 'react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { invoicesApi, quotesApi, contactsApi, itemsApi, expensesApi } from '@/core/adapter-api';
import { FileText, Users, ShoppingCart, TrendingUp, Settings, Upload, Package, ArrowUpRight, ArrowDownRight, Euro, Clock, CheckCircle, AlertTriangle, Zap, Power } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart } from 'recharts';

const fmt = (n: number) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const StatCard = ({ label, value, icon: Icon, color, sub, trend, onClick }: any) => (
    <Card onClick={onClick} className={`p-6 flex items-center gap-6 ${onClick ? 'cursor-pointer hover:animate-engine-rev' : ''}`}>
        <div className={`p-4 bg-[#050505] border-[var(--border-color)] border-2 cut-corner flex-shrink-0 ring-1 ring-white/5 shadow-[inset_0_0_15px_rgba(0,0,0,1)]`}>
            <Icon size={32} className={color} />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-sm text-[var(--text-secondary)] font-display tracking-[0.25em] drop-shadow-[0_2px_2px_#000]">{label}</p>
            <h3 className="text-5xl font-display font-bold text-[var(--text-primary)] truncate mt-1 drop-shadow-[0_2px_4px_#000]">{value}</h3>
            {sub && <p className={`text-xs mt-2 font-mono tracking-tight font-bold ${trend === 'up' ? 'text-[var(--accent)] drop-shadow-[0_0_8px_var(--accent-glow)]' : trend === 'down' ? 'text-red-500' : 'text-[var(--text-muted)]'}`}>=&gt; {sub}</p>}
        </div>
    </Card>
);

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#030303] border-4 border-[var(--accent)] cut-corner shadow-[0_0_30px_var(--accent-glow)] p-5 text-sm">
                <p className="font-display font-bold text-[var(--accent)] mb-3 tracking-[0.2em] text-2xl border-b-2 border-[var(--accent)]/30 pb-2">{label}</p>
                {payload.map((p: any) => (
                    <p key={p.name} style={{ color: p.color }} className="font-mono text-lg font-bold">
                        <span className="opacity-50 mr-2">SYS.</span>{p.name === 'ingresos' ? 'REV: ' : 'EXP: '}{fmt(p.value)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

const Dashboard: React.FC = () => {
    const navigate = useNavigate();

    const invoiceCount = useQuery(() => invoicesApi.count(), [], ['invoices']) ?? 0;
    const quoteCount = useQuery(() => quotesApi.count(), [], ['quotes']) ?? 0;
    const contactCount = useQuery(() => contactsApi.count(), [], ['contacts']) ?? 0;
    const itemCount = useQuery(() => itemsApi.count(), [], ['items']) ?? 0;

    const allInvoices = useQuery(() => invoicesApi.all(), [], ['invoices']);
    const allExpenses = useQuery(() => expensesApi.all(), [], ['expenses']);
    const allQuotes = useQuery(() => quotesApi.all(), [], ['quotes']);

    const stats = useMemo(() => {
        if (!allInvoices || !allExpenses || !allQuotes) return null;
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

        const isMonth = (ts: number, m: number, y: number) => {
            const d = new Date(ts);
            return d.getMonth() === m && d.getFullYear() === y;
        };

        const paidInvoices = allInvoices.filter(i => i.status === 'paid');
        const pendingInvoices = allInvoices.filter(i => ['sent', 'overdue'].includes(i.status));
        const overdueInvoices = allInvoices.filter(i => i.status === 'overdue');

        const thisMonthRevenue = paidInvoices
            .filter(i => isMonth(Number(i.date || i.createdAt) || 0, thisMonth, thisYear))
            .reduce((a, i) => a + (i.grandTotal || 0), 0);
        const lastMonthRevenue = paidInvoices
            .filter(i => isMonth(Number(i.date || i.createdAt) || 0, lastMonth, lastMonthYear))
            .reduce((a, i) => a + (i.grandTotal || 0), 0);

        const totalRevenue = paidInvoices.reduce((a, i) => a + (i.grandTotal || 0), 0);
        const pendingAmount = pendingInvoices.reduce((a, i) => a + (i.grandTotal || 0), 0);
        const thisMonthExpenses = allExpenses
            .filter(e => isMonth(Number(e.date) || 0, thisMonth, thisYear))
            .reduce((a, e) => a + (e.total || 0), 0);

        // Conversion rate: accepted + invoiced quotes / total quotes
        const convertedQuotes = allQuotes.filter(q => ['accepted', 'invoiced'].includes(q.status)).length;
        const convRate = allQuotes.length > 0 ? Math.round((convertedQuotes / allQuotes.length) * 100) : 0;

        // Last 6 months chart data
        const chartData = Array.from({ length: 6 }, (_, i) => {
            const monthIdx = (thisMonth - 5 + i + 12) % 12;
            const yearIdx = thisMonth - 5 + i < 0 ? thisYear - 1 : thisYear;
            const monthName = new Date(yearIdx, monthIdx, 1).toLocaleDateString('es-ES', { month: 'short' });
            const ingresos = paidInvoices
                .filter(inv => isMonth(Number(inv.date || inv.createdAt) || 0, monthIdx, yearIdx))
                .reduce((a, inv) => a + (inv.grandTotal || 0), 0);
            const gastos = allExpenses
                .filter(exp => isMonth(Number(exp.date) || 0, monthIdx, yearIdx))
                .reduce((a, exp) => a + (exp.total || 0), 0);
            return { mes: monthName.charAt(0).toUpperCase() + monthName.slice(1), ingresos, gastos };
        });

        const revTrend = lastMonthRevenue === 0 ? null : ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100).toFixed(0);

        return { thisMonthRevenue, lastMonthRevenue, totalRevenue, pendingAmount, overdueInvoices, pendingInvoices, thisMonthExpenses, convRate, chartData, revTrend };
    }, [allInvoices, allExpenses, allQuotes]);

    return (
        <div className="max-w-[90rem] mx-auto space-y-10">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between border-b-4 border-[#111] pb-6 relative">
                <div className="absolute top-0 left-0 w-32 h-1 bg-[var(--accent)] shadow-[0_0_15px_var(--accent-glow)]"></div>
                <div>
                    <h1 className="text-6xl font-display font-bold text-white tracking-[0.1em] uppercase flex items-center gap-4 mt-4 drop-shadow-[0_4px_10px_#000]">
                        <Power className="text-[var(--accent)] drop-shadow-[0_0_15px_var(--accent-glow)] animate-pulse" size={50} strokeWidth={2.5} /> 
                        SISTEMA PRINCIPAL
                    </h1>
                    <div className="flex items-center gap-3 mt-4">
                        <span className="animate-neon-pulse flex items-center justify-center w-4 h-4 rounded-none bg-[var(--accent)]"></span>
                        <p className="text-[var(--text-secondary)] font-mono font-bold uppercase tracking-widest text-sm bg-[#111] px-3 py-1 border border-[#222]">ENG: IGNITION_ON // STATUS: OPERACIONAL</p>
                    </div>
                </div>
                <div className="mt-6 md:mt-0 font-display text-4xl font-bold text-[#333] tracking-[0.2em] pointer-events-none select-none">
                    DSHBRD-01
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8">
                <StatCard label="FACTURAS" value={invoiceCount} icon={FileText} color="text-[var(--accent)]" onClick={() => navigate('/invoices')} />
                <StatCard label="PRESUPUESTOS" value={quoteCount} icon={TrendingUp} color="text-orange-500"
                    sub={stats ? `${stats.convRate}% WIN RATE` : ''} trend="up" onClick={() => navigate('/quotes')} />
                <StatCard label="CLIENTES" value={contactCount} icon={Users} color="text-emerald-500" onClick={() => navigate('/contacts')} />
                <StatCard label="INVENTARIO" value={itemCount} icon={Package} color="text-cyan-500" onClick={() => navigate('/items')} />
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8">
                <Card className="p-8 border-t-4 border-t-[var(--accent)] bg-gradient-to-b from-[var(--bg-card)] to-[#000]">
                    <div className="flex items-center gap-3 mb-5">
                        <CheckCircle size={24} className="text-[var(--accent)]" />
                        <span className="text-sm font-display font-bold text-[var(--accent)] tracking-[0.2em] uppercase">RENDIMIENTO MES</span>
                    </div>
                    <p className="text-4xl font-mono font-bold text-white drop-shadow-[0_2px_4px_#000]">{stats ? fmt(stats.thisMonthRevenue) : '—'}</p>
                    {stats?.revTrend && (
                        <p className={`text-sm mt-3 font-mono font-bold flex items-center gap-2 ${Number(stats.revTrend) >= 0 ? 'text-emerald-500 bg-emerald-950/50 border border-emerald-900 px-2 py-1 inline-flex' : 'text-red-500 bg-red-950/50 border border-red-900 px-2 py-1 inline-flex'}`}>
                            {Number(stats.revTrend) >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                            {Math.abs(Number(stats.revTrend))}% VS CICLO ANT.
                        </p>
                    )}
                </Card>
                <Card className="p-8">
                    <div className="flex items-center gap-3 mb-5">
                        <Clock size={24} className="text-amber-500" />
                        <span className="text-sm font-display font-bold text-[var(--text-secondary)] tracking-[0.2em] uppercase">SALDO PENDIENTE</span>
                    </div>
                    <p className="text-4xl font-mono font-bold text-white drop-shadow-[0_2px_4px_#000]">{stats ? fmt(stats.pendingAmount) : '—'}</p>
                    {stats && <p className="text-sm text-[var(--text-muted)] font-mono font-bold mt-3 uppercase tracking-widest">&gt; {stats.pendingInvoices.length} REGISTROS</p>}
                </Card>
                <Card className="p-8 hover-glitch border-l-4 border-l-red-600 bg-red-950/10">
                    <div className="flex items-center gap-3 mb-5">
                        <AlertTriangle size={24} className="text-red-500 animate-pulse" />
                        <span className="text-sm font-display font-bold text-red-500 tracking-[0.2em] uppercase">CRÍTICO: VENCIDO</span>
                    </div>
                    <p className="text-4xl font-mono font-bold text-red-500 drop-shadow-[0_0_15px_rgba(239,68,68,0.8)]">{stats ? stats.overdueInvoices.length : '—'}</p>
                    {stats && stats.overdueInvoices.length > 0 && (
                        <p className="text-sm text-red-400 font-mono font-bold mt-3 uppercase tracking-widest">&gt; IMPACTO: {fmt(stats.overdueInvoices.reduce((a, i) => a + (i.grandTotal || 0), 0))}</p>
                    )}
                </Card>
                <Card className="p-8">
                    <div className="flex items-center gap-3 mb-5">
                        <Euro size={24} className="text-rose-500" />
                        <span className="text-sm font-display font-bold text-[var(--text-secondary)] tracking-[0.2em] uppercase">COSTOS/GASTOS</span>
                    </div>
                    <p className="text-4xl font-mono font-bold text-white drop-shadow-[0_2px_4px_#000]">{stats ? fmt(stats.thisMonthExpenses) : '—'}</p>
                    {stats && <p className="text-sm text-[var(--text-muted)] font-mono font-bold mt-3 uppercase tracking-widest">VS {fmt(stats.thisMonthRevenue)} CAJA</p>}
                </Card>
            </div>

            {/* Chart + Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-10">
                <Card className="lg:col-span-2 p-8 border-none bg-transparent shadow-none" noHover>
                    <div className="flex items-end justify-between mb-8 border-b-2 border-[#111] pb-4 caution-stripe relative overflow-hidden">
                         <div className="absolute inset-0 bg-[#000]/80 z-0"></div>
                         <h2 className="text-3xl font-display font-bold text-[var(--accent)] tracking-[0.2em] uppercase z-10 px-4">TELEMETRÍA DE INGRESOS (6M)</h2>
                         <div className="z-10 bg-[var(--accent)] text-[#000] font-mono font-bold px-3 py-1 text-sm tracking-widest mr-4">SYS.ANALYSIS</div>
                    </div>
                    
                    <div className="bg-[#050505] border-2 border-[var(--border-color)] p-6 cut-corner shadow-[inset_0_0_20px_#000]">
                        {stats && stats.chartData.some(d => d.ingresos > 0 || d.gastos > 0) ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={stats.chartData} barGap={4} barSize={34}>
                                    <CartesianGrid strokeDasharray="2 4" stroke="#222" vertical={false} />
                                    <XAxis dataKey="mes" tick={{ fontSize: 16, fill: 'var(--text-secondary)', fontFamily: 'Teko, sans-serif' }} axisLine={{stroke: '#333', strokeWidth: 2}} tickLine={false} dy={10} />
                                    <YAxis tick={{ fontSize: 13, fill: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 'bold' }} axisLine={{stroke: '#333', strokeWidth: 2}} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(245, 158, 11, 0.05)' }} />
                                    <Bar dataKey="ingresos" fill="var(--accent)" radius={0} />
                                    <Bar dataKey="gastos" fill="var(--bg-card)" stroke="#ef4444" strokeWidth={2} radius={0} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[280px] flex items-center justify-center text-[var(--text-muted)] text-base font-mono font-bold flex-col gap-5">
                                <TrendingUp size={64} className="text-[#222]" />
                                <p className="uppercase tracking-[0.2em]">SISTEMA ESPERANDO ENTRADAS</p>
                            </div>
                        )}
                        <div className="flex gap-8 mt-8 border-t border-[#111] pt-6 font-display uppercase tracking-[0.2em] font-bold text-lg">
                            <span className="flex items-center gap-3 text-white"><span className="w-5 h-5 bg-[var(--accent)] shadow-[0_0_15px_var(--accent-glow)] inline-block" />INGRESOS [REV]</span>
                            <span className="flex items-center gap-3 text-white"><span className="w-5 h-5 bg-[var(--bg-card)] border-2 border-red-500 inline-block" />GASTOS [EXP]</span>
                        </div>
                    </div>
                </Card>

                <Card className="p-8 border-t-8 border-t-[var(--border-color)]">
                    <h2 className="text-3xl font-display font-bold text-white tracking-[0.2em] uppercase mb-8 border-l-4 border-[var(--text-muted)] pl-4">COMANDOS RÁPIDOS</h2>
                    <div className="space-y-4">
                        {[
                            { path: '/invoices', icon: FileText, label: 'NUEVA FACTURA [CMD+1]', hover: 'hover:border-amber-500 hover:text-amber-500 hover:bg-amber-500/10' },
                            { path: '/quotes', icon: ShoppingCart, label: 'PRESUPUESTO [CMD+2]', hover: 'hover:border-orange-500 hover:text-orange-500 hover:bg-orange-500/10' },
                            { path: '/contacts', icon: Users, label: 'DIR. CLIENTES [CMD+3]', hover: 'hover:border-emerald-500 hover:text-emerald-500 hover:bg-emerald-500/10' },
                            { path: '/expenses', icon: Zap, label: 'REGISTRO GASTO [CMD+4]', hover: 'hover:border-red-500 hover:text-red-500 hover:bg-red-500/10' },
                            { path: '/settings', icon: Settings, label: 'CONFIG. GLOBLAL [SYS]', hover: 'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10' },
                        ].map(({ path, icon: Icon, label, hover }) => (
                            <button key={path} onClick={() => navigate(path)}
                                className={`w-full flex items-center gap-5 px-6 py-5 border-2 border-[var(--border-color)] bg-[#030303] text-[var(--text-secondary)] font-display uppercase tracking-widest text-lg font-bold transition-all duration-300 text-left ${hover} cut-corner btn-rock`}>
                                <Icon size={22} className="opacity-80" /> {label}
                            </button>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default Dashboard;
